<script setup lang="ts">
import { XModal, XIcon } from '@clicksign/excalibur';
import { useRouteQuery } from '@vueuse/router';
import { useI18n } from '#i18n';

import { BrandButton } from '~~base-ui';
import { APP_ROUTES, useEnvironmentInfo } from '~~core';

import { useRequirementHelpLink } from '../../composables/use-requirement-help-link';

type Option = {
  name: string;
  icon: string;
  text: string;
  to?: string;
  external?: boolean;
  dataTestid?: string;
  iconStyle?: string;
  onClick?: () => void;
};

export type RequirementOptionsModalProps = {
  showRefusalOption?: boolean;
};

const props = withDefaults(defineProps<RequirementOptionsModalProps>(), {
  showRefusalOption: false,
});

export type RequirementOptionsModalEmits = {
  (e: 'close'): void
  (e: 'refuse'): void
};

const emit = defineEmits<RequirementOptionsModalEmits>();
const { t } = useI18n();
const gtm = useGtm();
const isFromSignerArea = useRouteQuery('from_signer_area', undefined, { transform: (value: unknown) => value !== undefined }); // Backwards compatibility
const { isEmbedded } = useEnvironmentInfo();
const { currentHelpLink } = useRequirementHelpLink();

const options = computed<Option[]>(() => [
  {
    name: 'help',
    icon: 'question-circle',
    text: t('common.help'),
    to: currentHelpLink.value,
  },

  isFromSignerArea.value && !isEmbedded.value && {
    name: 'dashboard',
    icon: 'arrow-left',
    text: t('requirement.toolbar.mySignerArea'),
    to: APP_ROUTES.signerArea,
    external: true,
    dataTestid: 'requirementOptionsModalSignerAreaButton',
  },

  props.showRefusalOption && {
    name: 'refusal',
    text: t('requirement.refusal.modal.refuse'),
    icon: 'close',
    iconStyle: 'fak',
    dataTestid: 'requirementOptionsModalRefusalButton',
    onClick: () => emit('refuse'),
  },
].filter(Boolean));

function dispatchGAEvent(event: string) {
  const eventName = `DropButton${event.replace(/^\w/, (char) => char.toUpperCase())}Click`;

  gtm?.trackEvent({
    event: 'ga-send',
    ga_event_category: 'sign-step-widget-app',
    ga_event_name: eventName,
  });
}

function handleOptionClick(item: Option) {
  dispatchGAEvent(item.name);
  item.onClick?.();
}
</script>

<template>
  <XModal
    is-open
    :title="$t('common.options')"
    close-button
    width="400"
    data-testid="requirementOptionsModal"
    @close="$emit('close')"
  >
    <template #default>
      <ul :class="$style.options">
        <li
          v-for="option in options"
          :key="option.name"
        >
          <BrandButton
            full-width
            design="outlined"
            target="_blank"
            :external="option.external"
            :class="$style.btn"
            :to="option.to"
            :data-testid="option.dataTestid"
            @click="handleOptionClick(option)"
          >
            <XIcon
              :icon="option.icon"
              :icon-style="option.iconStyle"
              fixed-width
              left
            />

            {{ option.text }}
          </BrandButton>
        </li>
      </ul>
    </template>

    <template #actions>
      <BrandButton
        full-width
        design="outlined"
        data-testid="requirementOptionsModalActionCloseButton"
        @click="$emit('close')"
      >
        {{ $t('common.close') }}
      </BrandButton>
    </template>
  </XModal>
</template>

<style module lang="scss">
.options {
  display: flex;
  flex-direction: column;
  padding-block: var(--space-regular) var(--space-large);
  row-gap: var(--space-regular);
  list-style-type: none;

  .btn {
    justify-content: flex-start;
  }
}
</style>
